import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Handle, Position, NodeToolbar, applyNodeChanges, applyEdgeChanges } from '@xyflow/react';
import {
  LuArrowRightToLine, LuArrowRightFromLine, LuPlus, LuTrash2, LuFlag,
  LuBot, LuMessagesSquare, LuFolderGit2, LuCircleDot,
} from 'react-icons/lu';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import dagre from 'dagre';
import GraphCanvas from '../../components/graphCanvas';
import WorkflowEdge from './workflowEdge';

/**
 * WorkflowGraph — renders a cell-workflow TEMPLATE as a left-to-right editable step graph, built on the
 * same framework as the mission graph (surfaces/cells/missionGraph.js).
 *
 * A template is { name, start, nodes: [{ id, ty, ... }], edges: [{ from, to, when? }], defaults }. The
 * `nodes` are STEPS (each has an id + a `ty` in worker_task | external_input | workspace_init) and the
 * `edges` are FLOW connectors — the arrow points FROM a step TO the NEXT step (a normal flow arrow, NOT
 * the depends_on/arrow-on-prerequisite convention the mission graph uses). `start` is the entry step.
 *
 * Steps lay out by a dagre LR pass (the start ranks LEFT, flow runs rightward); nodes are draggable for
 * a transient nudge and re-layout deterministically when the template changes. Selecting a node reveals a
 * ReactFlow <NodeToolbar> with two blobhub-styled icon buttons — add a next step that flows from this one,
 * and remove this step. Each node exposes two connection handles, each carrying a small right-pointing
 * direction icon: a LEFT handle (flow IN — the previous step arrives here) and a RIGHT handle (flow OUT —
 * toward the next step). A new flow edge is drawn by dragging from a handle and releasing it ON another
 * node's handle; it renders as a bezier arrow ANCHORED to those handles — it leaves the from-step's RIGHT
 * handle and enters the to-step's LEFT handle, so the arrowhead lands on the NEXT step (see workflowEdge.js).
 *
 * Editing model — fully incremental (mirrors the mission graph):
 *   - The canvas owns ReactFlow node/edge STATE, seeded from the template and re-seeded whenever the
 *     template-derived signature (step ids/types, start, edges, selection) changes. Live interactions
 *     (drag, selection) are applied to that state via applyNodeChanges / applyEdgeChanges. Step positions
 *     are NOT persisted to the template — the flow re-derives its LR layout on every structural change.
 *   - Delete (the toolbar's remove button, or Delete/Backspace on a selection) removes the selected step
 *     or flow edge, captured at document level so it never bubbles to the outer playground canvas.
 *
 * Flow direction convention:
 *   An edge { from, to } means `from` runs first and `to` is the next step. The edge object carries
 *   source = from and target = to; the arrowhead lands ON the target (the next step), reading left-to-right.
 *
 * Handle-drag semantics (connectionMode "loose", so either handle initiates a drag):
 *   - Dragged from the RIGHT (out) handle → THIS node flows to the dropped node →
 *     onAddEdge(source = this, target = dropped).
 *   - Dragged from the LEFT (in) handle → the dropped node flows INTO this node →
 *     onAddEdge(from = dropped, to = this).
 *   (See flowForConnection.)
 *
 * Props:
 *   workflow       — the template object (or null)
 *   selectedStep   — currently selected step id or null
 *   selectedEdgeId — currently selected flow-edge id (`e-from-to`) or null
 *   onSelectStep   — (stepId|null) => void
 *   onSelectEdge   — (edgeInfo) => void                 (optional; enables edge selection)
 *   onAddEdge      — (from, to) => void                 (optional; enables drag-to-connect)
 *   onAddStep      — (fromStepId) => void               (optional; toolbar "add next step")
 *   onRemoveStep   — (stepId) => void                   (optional; toolbar remove + delete key)
 *   onRemoveEdge   — (from, to) => void                 (optional; enables edge delete)
 */

const NODE_WIDTH = 180;
// Tall enough for the step-id line plus the pill row. Fed to dagre purely for row spacing of the LR
// layout (the handle-anchored edges no longer depend on it).
const NODE_HEIGHT = 64;

// Each step exposes exactly two handles, identified by side. A flow edge can be drawn from either:
// dragging from the RIGHT handle makes THIS step the `from` (flow out); dragging from the LEFT handle
// makes THIS step the `to` (flow in). connectionMode is "loose" so either initiates (see flowForConnection).
const LEFT_HANDLE_ID = 'left';
const RIGHT_HANDLE_ID = 'right';

// Default step type for a freshly added step.
export const STEP_FALLBACK_TYPE = 'worker_task';

// Sentinel terminal marker used by the cell-workflow runtime (an edge may target '@done' to end a flow).
// Rendered as a non-interactive terminal node only when an edge actually points to it, so it always has an
// anchor; never stored as a real step in `nodes`.
const DONE_ID = '@done';

// Small leading icon per step type, distinguishing the type pill the way the mission node's pill icons do.
const STEP_TYPE_ICON = {
  worker_task: LuBot,
  external_input: LuMessagesSquare,
  workspace_init: LuFolderGit2,
};

// Defined outside the component so the references are stable across renders.
const NODE_TYPES = { step: StepNode, done: DoneNode };
const EDGE_TYPES = { flow: WorkflowEdge };

/**
 * StepNode — compact bh-node card for a workflow step: the step id (bold) + a pill row (a `start` chip on
 * the entry step, then the step-type pill). A LEFT and a RIGHT handle (each with a right-pointing flow
 * icon) drive flow wiring. When the node is the sole selection, a <NodeToolbar> floats above it with two
 * icon buttons (add next step / remove), each a blobhub light button with a tooltip — the same compact
 * treatment as the mission node toolbar.
 */
function StepNode({ id, data, isConnectable, selected }) {
  const { stepId, stepType, isStart, onAddStep, onRemove } = data;
  const nodeId = id || stepId;

  const stop = (e) => { e.stopPropagation(); };
  const handleAdd = (e) => { e.stopPropagation(); if (onAddStep) onAddStep(nodeId); };
  const handleRemove = (e) => { e.stopPropagation(); if (onRemove) onRemove(nodeId); };

  const hasToolbar = onAddStep || onRemove;
  const TypeIcon = STEP_TYPE_ICON[stepType] || LuCircleDot;

  return (
    <div className={'bh-node mc-wf-step-node' + (selected ? ' bh-node-selected' : '')}>
      {/* Selection toolbar — shown only while this node is the sole selection (NodeToolbar default).
          Each button is a react-bootstrap light button with an icon + tooltip. */}
      {hasToolbar && (
        <NodeToolbar nodeId={nodeId} position={Position.Top} offset={8} className="mc-wf-toolbar">
          {onAddStep && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`mc-tt-addstep-${nodeId}`}>Add next step</Tooltip>}>
              <Button
                variant="light"
                size="sm"
                className="mc-wf-toolbar-btn"
                aria-label="Add next step"
                onMouseDown={stop}
                onClick={handleAdd}
              >
                <LuPlus size={14} />
              </Button>
            </OverlayTrigger>
          )}
          {onRemove && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`mc-tt-removestep-${nodeId}`}>Remove step</Tooltip>}>
              <Button
                variant="light"
                size="sm"
                className="mc-wf-toolbar-btn mc-wf-toolbar-remove"
                aria-label="Remove step"
                onMouseDown={stop}
                onClick={handleRemove}
              >
                <LuTrash2 size={14} />
              </Button>
            </OverlayTrigger>
          )}
        </NodeToolbar>
      )}

      {/* LEFT handle = flow IN (the previous step arrives here); RIGHT handle = flow OUT (toward the next
          step). Both icons point right, with the flow. The icons are pointer-events:none (workflows.css)
          so they never block the connect-drag. */}
      <Handle
        type="target"
        position={Position.Left}
        id={LEFT_HANDLE_ID}
        className="mc-wf-handle mc-wf-handle-left"
        isConnectable={isConnectable}
      >
        <LuArrowRightToLine className="mc-wf-handle-icon" size={9} />
      </Handle>
      <Handle
        type="source"
        position={Position.Right}
        id={RIGHT_HANDLE_ID}
        className="mc-wf-handle mc-wf-handle-right"
        isConnectable={isConnectable}
      >
        <LuArrowRightFromLine className="mc-wf-handle-icon" size={9} />
      </Handle>

      <div className="bh-node-name mc-wf-step-title">{stepId}</div>
      <div className="mc-wf-pills">
        {isStart && (
          <Badge variant="secondary" className="title-tag-badge mc-wf-start-pill">
            <LuFlag className="mc-wf-pill-icon" size={9} />
            start
          </Badge>
        )}
        <Badge variant="secondary" className="title-tag-badge mc-wf-type-pill" title={stepType}>
          <TypeIcon className="mc-wf-pill-icon" size={9} />
          <span className="mc-wf-pill-text">{stepType}</span>
        </Badge>
      </div>
    </div>
  );
}

/**
 * DoneNode — non-interactive terminal sentinel for an edge that targets '@done'. Styled as a dashed,
 * muted bh-node with a single LEFT target handle so the inbound flow edge has an anchor; not selectable,
 * draggable, or connectable.
 */
function DoneNode() {
  return (
    <div className="bh-node mc-wf-step-node mc-wf-done-node">
      <Handle
        type="target"
        position={Position.Left}
        id={LEFT_HANDLE_ID}
        className="mc-wf-handle mc-wf-handle-left"
        isConnectable={false}
      />
      <div className="bh-node-name mc-wf-done-name">@done</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers — template reducers and graph shaping (exported for unit tests)
// ---------------------------------------------------------------------------

/** Stable ReactFlow id for a flow edge — derived from its from/to pair. */
export function edgeFlowId(edge) {
  return `e-${edge.from}-${edge.to}`;
}

/** Recover the { from, to } a flow-edge write needs from a ReactFlow edge object. */
function edgeInfoFromRf(e) {
  const d = e.data || {};
  return {
    from: d.from != null ? d.from : e.source,
    to: d.to != null ? d.to : e.target,
  };
}

/** Next unused `step_N` id for a template (lowest N that is free). */
export function nextStepId(template) {
  const ids = new Set(((template && template.nodes) || []).map(n => n.id));
  let n = 1;
  while (ids.has(`step_${n}`)) n++;
  return `step_${n}`;
}

/** The flow "tail" — the last step (in node order) with no outgoing edge, or null for an empty template. */
export function tailStepId(template) {
  const nodes = (template && template.nodes) || [];
  if (nodes.length === 0) return null;
  const hasOutgoing = new Set(((template && template.edges) || []).map(e => e.from));
  for (let i = nodes.length - 1; i >= 0; i--) {
    if (!hasOutgoing.has(nodes[i].id)) return nodes[i].id;
  }
  return nodes[nodes.length - 1].id;
}

/**
 * Add a step to a template. Appends a new worker_task node and, when fromStepId is given, a flow edge
 * fromStepId → newId (deduped). The first step added to an empty template becomes the entry (`start`).
 * Returns { template: newTemplate, newId }; pure (never mutates the input).
 */
export function addStep(template, fromStepId) {
  const base = template || {};
  const nodes = base.nodes || [];
  const edges = base.edges || [];
  const newId = nextStepId(base);
  const nextNodes = [...nodes, { id: newId, ty: STEP_FALLBACK_TYPE }];

  let nextEdges = edges;
  if (fromStepId && fromStepId !== DONE_ID) {
    const exists = edges.some(e => e.from === fromStepId && e.to === newId);
    if (!exists) nextEdges = [...edges, { from: fromStepId, to: newId }];
  }

  const nextTemplate = { ...base, nodes: nextNodes, edges: nextEdges };
  if (nodes.length === 0) nextTemplate.start = newId;
  return { template: nextTemplate, newId };
}

/**
 * Remove a step (and its incident flow edges) from a template. Guards against emptying the graph — the
 * last remaining step is never removed. If the removed step was the entry, `start` is reassigned to a flow
 * root (a remaining step with no incoming edge) or the first remaining step. Returns { template, removed };
 * pure (returns the input template unchanged when nothing is removed).
 */
export function removeStep(template, stepId) {
  const base = template || {};
  const nodes = base.nodes || [];
  if (nodes.length <= 1) return { template: base, removed: false };
  if (!nodes.some(n => n.id === stepId)) return { template: base, removed: false };

  const nextNodes = nodes.filter(n => n.id !== stepId);
  const nextEdges = (base.edges || []).filter(e => e.from !== stepId && e.to !== stepId);

  let start = base.start;
  if (start === stepId) {
    const hasIncoming = new Set(nextEdges.map(e => e.to));
    const root = nextNodes.find(n => !hasIncoming.has(n.id));
    start = (root || nextNodes[0]).id;
  }

  return { template: { ...base, nodes: nextNodes, edges: nextEdges, start }, removed: true };
}

/**
 * Map a ReactFlow connection to the (from, to) pair a flow-edge write needs, branching on which handle
 * started the drag. Returns null for an incomplete connection.
 *
 *   - RIGHT handle (or unknown) → THIS node (connection.source) flows to the dropped node (target):
 *                                 { from: source, to: target }.
 *   - LEFT handle               → the dropped node (target) flows INTO this node (source):
 *                                 { from: target, to: source }.
 */
export function flowForConnection(connection) {
  if (!connection || !connection.source || !connection.target) return null;
  if (connection.sourceHandle === LEFT_HANDLE_ID) {
    return { from: connection.target, to: connection.source };
  }
  return { from: connection.source, to: connection.target };
}

/**
 * Template-derived re-seed signature. Depends only on step ids/types, the entry step, the flow edges, and
 * the current step/edge selection — never on per-step properties (prompt, agent, …), so editing a step's
 * properties never re-seeds or re-layouts the graph.
 */
export function graphSignature(template, selectedStep, selectedEdgeId) {
  if (!template) return '##';
  const nodesSig = (template.nodes || []).map(n => `${n.id}:${n.ty || n.type || ''}`).join(',');
  const edgesSig = (template.edges || []).map(e => `${e.from}->${e.to}:${e.when || ''}`).join(',');
  return `${template.start || ''}|${nodesSig}#${edgesSig}#${selectedStep || ''}#${selectedEdgeId || ''}`;
}

/**
 * Build a dagre LR layout for the given step ids over the flow edges. Edge direction: from → to, so each
 * step ranks to the LEFT of the steps it flows into (the entry step ends up leftmost). Returns
 * { [id]: { x, y } }.
 */
function computeDagreLayout(ids, flowEdges) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 130, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  const idSet = new Set(ids);
  for (const id of ids) g.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  for (const e of (flowEdges || [])) {
    if (idSet.has(e.from) && idSet.has(e.to)) g.setEdge(e.from, e.to); // from ranks LEFT of to
  }

  dagre.layout(g);

  const positions = {};
  for (const id of ids) {
    const node = g.node(id);
    if (node) positions[id] = { x: node.x - NODE_WIDTH / 2, y: node.y - NODE_HEIGHT / 2 };
  }
  return positions;
}

/**
 * Build the ReactFlow nodes and edges arrays from a workflow template. Steps lay out by a dagre LR pass;
 * an edge that targets '@done' adds a non-interactive terminal node so the flow keeps its endpoint. Only
 * edges whose endpoints both render are kept, and duplicate (from,to) pairs collapse to one connector.
 */
export function buildElements(template, selectedStep, selectedEdgeId) {
  if (!template || !template.nodes || template.nodes.length === 0) return { nodes: [], edges: [] };

  const steps = template.nodes;
  const flowEdges = template.edges || [];
  const start = template.start || (steps[0] && steps[0].id);

  const stepIds = new Set(steps.map(s => s.id));
  const usesDone = flowEdges.some(e => e.to === DONE_ID && stepIds.has(e.from));

  const layoutIds = steps.map(s => s.id);
  if (usesDone) layoutIds.push(DONE_ID);
  const positions = computeDagreLayout(layoutIds, flowEdges);

  const nodes = steps.map(s => ({
    id: s.id,
    type: 'step',
    position: positions[s.id] || { x: 0, y: 0 },
    selected: s.id === selectedStep,
    draggable: true,
    data: {
      stepId: s.id,
      stepType: s.ty || s.type || STEP_FALLBACK_TYPE,
      isStart: s.id === start,
    },
    style: { width: NODE_WIDTH },
  }));

  if (usesDone) {
    nodes.push({
      id: DONE_ID,
      type: 'done',
      position: positions[DONE_ID] || { x: 0, y: 0 },
      data: {},
      selectable: false,
      draggable: false,
      style: { width: 96 },
    });
  }

  const renderable = new Set(stepIds);
  if (usesDone) renderable.add(DONE_ID);

  const seen = new Set();
  const edges = flowEdges
    .filter(e => renderable.has(e.from) && renderable.has(e.to))
    .filter(e => {
      const key = `${e.from}->${e.to}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(e => {
      const id = edgeFlowId(e);
      return {
        id,
        source: e.from,                 // current step
        target: e.to,                   // next step (arrowhead lands here)
        sourceHandle: RIGHT_HANDLE_ID,  // from-step's RIGHT (out) handle
        targetHandle: LEFT_HANDLE_ID,   // to-step's LEFT (in) handle
        selected: !!selectedEdgeId && id === selectedEdgeId,
        type: 'flow',
        animated: true,
        data: { from: e.from, to: e.to, label: e.when || undefined },
      };
    });

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// WorkflowGraph — the React Flow canvas (mirrors MissionGraph's seed/re-seed,
// document-capture Delete guard, and node-toolbar decoration)
// ---------------------------------------------------------------------------

function WorkflowGraph({
  workflow,
  selectedStep,
  selectedEdgeId,
  onSelectStep,
  onSelectEdge,
  onAddEdge,
  onAddStep,
  onRemoveStep,
  onRemoveEdge,
}) {
  // Template-derived signature — drives a re-seed when the structure (not a live, in-canvas interaction)
  // changes. A function of step ids/types/start/edges/selection ONLY, so a step-property edit never
  // re-seeds or re-layouts the graph.
  const sig = useMemo(
    () => graphSignature(workflow, selectedStep, selectedEdgeId),
    [workflow, selectedStep, selectedEdgeId]
  );

  const [rfNodes, setRfNodes] = useState(
    () => buildElements(workflow, selectedStep, selectedEdgeId).nodes
  );
  const [rfEdges, setRfEdges] = useState(
    () => buildElements(workflow, selectedStep, selectedEdgeId).edges
  );

  // Re-seed from the template whenever the signature changes (skip the redundant mount run). Re-running
  // dagre is deterministic, so a re-seed never jostles unchanged nodes.
  const lastSig = useRef(sig);
  useEffect(() => {
    if (lastSig.current === sig) return;
    lastSig.current = sig;
    const built = buildElements(workflow, selectedStep, selectedEdgeId);
    setRfNodes(built.nodes);
    setRfEdges(built.edges);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs so the once-attached keydown listener always sees the latest state/handlers.
  const rfNodesRef = useRef(rfNodes);
  const rfEdgesRef = useRef(rfEdges);
  const onRemoveStepRef = useRef(onRemoveStep);
  const onRemoveEdgeRef = useRef(onRemoveEdge);
  rfNodesRef.current = rfNodes;
  rfEdgesRef.current = rfEdges;
  onRemoveStepRef.current = onRemoveStep;
  onRemoveEdgeRef.current = onRemoveEdge;

  // Own Delete/Backspace at document capture so it removes the SELECTED step/edge here instead of bubbling
  // to the outer playground canvas (which would delete the whole widget). Mirrors the mission graph. Only
  // steals the key when there is something we can actually delete.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (target && target.isContentEditable)) return;
      const selNodes = rfNodesRef.current.filter(n => n.selected && n.type === 'step');
      const selEdges = rfEdgesRef.current.filter(e => e.selected);
      const delNodes = selNodes.length > 0 && onRemoveStepRef.current;
      const delEdges = selEdges.length > 0 && onRemoveEdgeRef.current;
      if (!delNodes && !delEdges) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      if (delNodes) selNodes.forEach(n => onRemoveStepRef.current(n.id));
      if (delEdges) selEdges.forEach(e => { const i = edgeInfoFromRf(e); onRemoveEdgeRef.current(i.from, i.to); });
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Inject the per-node toolbar handlers (stable from the surface) into the step nodes' data for StepNode.
  const decoratedNodes = useMemo(
    () => rfNodes.map(n => (
      n.type === 'step'
        ? { ...n, data: { ...n.data, onAddStep, onRemove: onRemoveStep } }
        : n
    )),
    [rfNodes, onAddStep, onRemoveStep]
  );

  const handleNodesChange = (changes) => setRfNodes(prev => applyNodeChanges(changes, prev));
  const handleEdgesChange = (changes) => setRfEdges(prev => applyEdgeChanges(changes, prev));

  const handleNodeClick = (node) => {
    if (node.id === DONE_ID) return;
    if (onSelectStep) onSelectStep(node.id);
  };

  const handleEdgeClick = (edge) => {
    if (onSelectEdge) onSelectEdge(edgeInfoFromRf(edge));
  };

  // Clicking the empty canvas deselects the step/edge.
  const handlePaneClick = () => {
    if (onSelectStep) onSelectStep(null);
  };

  // ReactFlow connection: map it to (from, to) by which handle started the drag.
  const handleConnect = (connection) => {
    const flow = flowForConnection(connection);
    if (onAddEdge && flow) onAddEdge(flow.from, flow.to);
  };

  if (!workflow || !workflow.nodes || workflow.nodes.length === 0) {
    return (
      <div className="mc-wf-graph-empty">
        <span>No steps yet — use &ldquo;Add step&rdquo; to begin this workflow.</span>
      </div>
    );
  }

  return (
    <div className="mc-wf-graph-canvas">
      <GraphCanvas
        nodes={decoratedNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        connectionMode="loose"
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onNodeClick={handleNodeClick}
        onEdgeClick={onSelectEdge ? handleEdgeClick : undefined}
        onPaneClick={handlePaneClick}
        onConnect={onAddEdge ? handleConnect : undefined}
        fitView={true}
      />
    </div>
  );
}

export default WorkflowGraph;
